<template>
  <div class="page">
    <div class="toolbar">
      <div class="flex1"></div>
      <el-button type="success" @click="openCreate">创建 API 密钥</el-button>
    </div>

    <el-alert type="info" show-icon :closable="false" class="mb12">
      <template #title>
        <div>Secret Key 仅在创建时显示一次，请妥善保存。</div>
      </template>
    </el-alert>

    <el-table :data="items" v-loading="loading" size="small" border>
      <el-table-column prop="name" label="名称" min-width="160" />
      <el-table-column prop="access_key" label="Access Key" min-width="240">
        <template #default="scope">
          <el-input v-model="scope.row.access_key" readonly size="small">
            <template #append>
              <el-button @click="copy(scope.row.access_key)" size="small"
                >复制</el-button
              >
            </template>
          </el-input>
        </template>
      </el-table-column>
      <el-table-column prop="is_active" label="状态" width="90">
        <template #default="scope">
          <el-tag :type="scope.row.is_active ? 'success' : 'info'">{{
            scope.row.is_active ? "启用" : "停用"
          }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" min-width="160" />
      <el-table-column prop="last_used_at" label="最近使用" min-width="160" />
      <el-table-column label="操作" width="140" fixed="right">
        <template #default="scope">
          <el-popconfirm
            title="确认删除该密钥？"
            @confirm="removeKey(scope.row)"
          >
            <template #reference>
              <el-button size="small" type="danger">删除</el-button>
            </template>
          </el-popconfirm>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog v-model="createVisible" title="创建 API 密钥" width="520px">
      <el-form :model="createForm" label-width="100px">
        <el-form-item label="名称">
          <el-input v-model="createForm.name" />
        </el-form-item>
        <el-form-item label="描述">
          <el-input v-model="createForm.description" type="textarea" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" @click="submitCreate">创建</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="onceVisible" title="请妥善保存您的密钥" width="560px">
      <el-alert type="warning" show-icon :closable="false" class="mb12">
        <template #title>
          <div>Secret Key 仅显示一次。关闭此弹窗后将无法再次查看。</div>
        </template>
      </el-alert>
      <el-form label-width="110px">
        <el-form-item label="Access Key">
          <el-input v-model="onceData.access_key" readonly>
            <template #append>
              <el-button @click="copy(onceData.access_key)">复制</el-button>
            </template>
          </el-input>
        </el-form-item>
        <el-form-item label="Secret Key">
          <el-input v-model="onceData.secret_key" readonly>
            <template #append>
              <el-button @click="copy(onceData.secret_key)">复制</el-button>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="onceVisible = false">我已保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, computed } from "vue";
import { ElMessage } from "element-plus";
import { useStore } from "vuex";
import { TenantServiceApi } from "@/services/dataService";

const store = useStore();
const currentUser = computed(() => store.getters.getUser);
const defaultTenantId = computed(() => currentUser.value?.tenant_id);

const effectiveTenantId = computed(() => defaultTenantId.value);

const loading = ref(false);
const items = ref<any[]>([]);

const createVisible = ref(false);
const createForm = reactive<{ name: string; description?: string }>({
  name: "",
  description: "",
});

const onceVisible = ref(false);
const onceData = reactive<{ access_key: string; secret_key: string }>({
  access_key: "",
  secret_key: "",
});

const copy = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    ElMessage.success("已复制到剪贴板");
  } catch {
    ElMessage.error("复制失败");
  }
};

const loadKeys = async () => {
  if (!effectiveTenantId.value) {
    ElMessage.warning("未检测到租户ID，请输入或使用管理员账号登录");
    return;
  }
  loading.value = true;
  try {
    const resp = await TenantServiceApi.listApiKeys(effectiveTenantId.value);
    items.value = resp?.data?.data || [];
  } catch (e: any) {
    ElMessage.error(e?.message || "加载失败");
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  if (!effectiveTenantId.value) {
    ElMessage.warning("请先提供租户ID");
    return;
  }
  createVisible.value = true;
  createForm.name = "";
  createForm.description = "";
};

const submitCreate = async () => {
  if (!effectiveTenantId.value) return;
  try {
    const resp = await TenantServiceApi.createApiKey(effectiveTenantId.value, {
      name: createForm.name,
      description: createForm.description,
    });
    const data = resp?.data?.data;
    if (data?.access_key && data?.secret_key) {
      onceData.access_key = data.access_key;
      onceData.secret_key = data.secret_key;
      onceVisible.value = true;
      createVisible.value = false;
      loadKeys();
      return;
    }
    ElMessage.error(resp?.data?.message || "创建失败");
  } catch (e: any) {
    ElMessage.error(e?.message || "创建失败");
  }
};

const removeKey = async (row: any) => {
  if (!effectiveTenantId.value) return;
  try {
    const resp = await TenantServiceApi.deleteApiKey(
      effectiveTenantId.value,
      row.id
    );
    if (resp?.data?.code === 0) {
      ElMessage.success("已删除");
      loadKeys();
    } else {
      ElMessage.error(resp?.data?.message || "删除失败");
    }
  } catch (e: any) {
    ElMessage.error(e?.message || "删除失败");
  }
};

onMounted(loadKeys);
</script>

<style scoped>
.page {
  padding: 16px;
}
.toolbar {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}
.flex1 {
  flex: 1;
}
.mr8 {
  margin-right: 8px;
}
.mb12 {
  margin-bottom: 12px;
}
</style>
